import { type ReactNode } from 'react';
import {
  Checklist,
  DossierTable,
  Field,
  FieldGroup,
  FieldProse,
  Gating,
  NumberedSection,
  Placeholder,
  RegaraNote,
  RegaraProseNote,
  Section16SoftwareDoc,
  Section17CyberInteropDoc,
  Section6SamdFields,
  SubsectionHeading,
  canonNA,
  obj,
  prose,
  withInlinePlaceholders,
} from './DossierPrimitives';
import { Ivd510kDeviceDescription, IvdPerformance, IvdShelfLife } from './IvdSections';

/**
 * eSTAR 510(k) renderer — all 22 sections per FDA Table 1 ordering.
 * Mirrors `renderEstarFromNewSchema` in `dossier-renderer.js`. Schema lives
 * in `backend/services/dossier_pdf.py` (Estar510kDossier).
 *
 * Schema detection (in the parent renderer): presence of
 * `section_01_submission_type` in the dossier dict.
 */
export function EstarRenderer({
  dossier,
  ivd = false,
}: {
  dossier: Record<string, unknown>;
  /** IVD eSTAR: swap §6 (assay/instrument), §14 (+stability), §19 (analytical
   *  battery) for their IVD variants — the 19 shared sections are reused.
   *  Mirrors the backend `section_overrides` / schema inheritance. */
  ivd?: boolean;
}) {
  return (
    <div className="space-y-4">
      <Section1 dossier={dossier} />
      <Section2 dossier={dossier} />
      <Section3 dossier={dossier} />
      <Section4 dossier={dossier} />
      <Section5 dossier={dossier} />
      {ivd ? (
        <Ivd510kDeviceDescription dossier={dossier} sectionKey="section_06_device_description" num={6} />
      ) : (
        <Section6 dossier={dossier} />
      )}
      <Section7 dossier={dossier} />
      <Section8 dossier={dossier} />
      <Section9 dossier={dossier} />
      <Section10 dossier={dossier} />
      <Section11 dossier={dossier} />
      <Section12 dossier={dossier} />
      <Section13 dossier={dossier} />
      {ivd ? (
        <IvdShelfLife dossier={dossier} sectionKey="section_14_shelf_life" num={14} />
      ) : (
        <Section14 dossier={dossier} />
      )}
      <Section15 dossier={dossier} />
      <Section16 dossier={dossier} />
      <Section17 dossier={dossier} />
      <Section18 dossier={dossier} />
      {ivd ? (
        <IvdPerformance dossier={dossier} sectionKey="section_19_performance_testing" num={19} />
      ) : (
        <Section19 dossier={dossier} />
      )}
      <Section20 dossier={dossier} />
      <Section21 dossier={dossier} />
      <Section22 dossier={dossier} />
    </div>
  );
}


// ── Section helpers ─────────────────────────────────────────────────────

function Section1({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_01_submission_type');
  return (
    <NumberedSection prefix="Section" num={1} title="Submission Type">
      <Field
        label="510(k) variant:"
        value={s.submission_type}
        fallbackHint="Traditional, Special, or Abbreviated 510(k)"
      />
    </NumberedSection>
  );
}

const _DESIGN_USE_QUESTIONS: [string, string][] = [
  ['Is the device intended for prescription use (21 CFR 801 Subpart D)?', 'prescription_use'],
  ['Is the device intended for over-the-counter use (21 CFR 807 Subpart C)?', 'otc_use'],
  ['Does the device contain components derived from a tissue or other biologic source?', 'biologic_source_components'],
  ['Is the device provided sterile?', 'provided_sterile'],
  ['Is the device intended for single use?', 'single_use'],
  ['Is the device a reprocessed single use device?', 'reprocessed_single_use'],
  ['If yes, does this device type require reprocessed validation data?', 'reprocessed_validation_required'],
  ['Does the device contain a drug?', 'contains_drug'],
  ['Does the device contain a biologic?', 'contains_biologic'],
  ['Does the device use software?', 'uses_software'],
  ['Does the submission include clinical information?', 'clinical_information_included'],
  ['Is the device implanted?', 'implanted'],
];

// Lockstep with backend/services/dossier/estar_renderer/_510k.py §2.
function Section2({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_02_cover_letter');
  const admin = obj(s, 'administrative_information');
  const wrap = (children: ReactNode) => (
    <NumberedSection prefix="Section" num={2} title="Cover Letter / Letters of Reference">
      {children}
    </NumberedSection>
  );
  // Backward-compat: a real (non-placeholder) prose ``body`` means the legacy
  // cover-letter shape (or an old stored dossier). The schema DEFAULTS
  // administrative_information to placeholders, so admin is populated even then
  // — prefer the prose body when genuinely present, else the letter would
  // silently render all-placeholders and drop the prose.
  const body = typeof s.body === 'string' ? s.body : '';
  const hasProse = body.trim().length > 0 && !body.trim().startsWith('__PLACEHOLDER__');
  if (Object.keys(admin).length === 0 || hasProse) {
    return wrap(
      <FieldProse label="Cover letter:" body={s.body} fallbackHint="Cover letter to FDA DCC" />,
    );
  }
  const cc = obj(admin, 'company_contact');
  const sc = obj(admin, 'submission_contact');
  const trade = Array.isArray(admin.device_trade_names) ? (admin.device_trade_names as unknown[]) : [];
  const du = obj(s, 'design_and_use');
  const adminRows: unknown[][] = [
    ['FDA Submission Document No.', admin.fda_submission_document_no],
    ['Type of 510(k) Submission', admin.submission_type_label],
    ['Address', admin.sponsor_address],
    ['Company Contact Person', cc.name],
    ['Company Contact Phone', cc.phone],
    ['Company Contact Email', cc.email],
    ['Establishment Registration', admin.establishment_registration],
    ['Submission Contact Person', sc.name],
    ['Submission Contact Phone', sc.phone],
    ['Submission Contact Email', sc.email],
    ['Device Common Name', admin.device_common_name],
    ['Device Trade/Proprietary Name', trade.length ? trade.map(String).join('; ') : null],
    ['Continued Confidentiality', 'Request made for continued confidentiality.'],
    ['Classification Name', admin.classification_name],
    ['Classification Regulation', admin.classification_regulation],
    ['Device Classification', admin.device_class],
    ['Device Panel', admin.device_panel],
    ['Device Product Code', admin.device_product_code],
    ['Prior FDA Document Numbers', admin.prior_fda_document_numbers],
  ];
  const duRows: unknown[][] = _DESIGN_USE_QUESTIONS.map(([q, k]) => [q, du[k]]);
  return wrap(
    <>
      <SubsectionHeading number="" title="FDA Addressee" />
      <div className="text-[13.5px] whitespace-pre-line">
        {'Food and Drug Administration\nCenter for Devices and Radiological Health\nDocument Mail Center - WO66-G609\n10903 New Hampshire Avenue\nSilver Spring, Maryland 20993-0002\nUnited States of America'}
      </div>
      <SubsectionHeading number="" title="Administrative Information" />
      <DossierTable headers={['Field', 'Value']} rows={adminRows} />
      <SubsectionHeading number="" title="Reason for the 510(k) Submission" />
      <FieldProse label="" body={s.reason_for_submission} fallbackHint="Reason for the 510(k) submission" />
      <SubsectionHeading number="" title="eSTAR Statement" />
      <div className="text-[13.5px]">
        This electronic signed cover letter is enclosed with an eSTAR PDF as an embedded
        attachment. We are submitting this eSTAR PDF via the FDA Customer Collaboration Portal (CCP).
      </div>
      <SubsectionHeading number="" title="Design and Use of the Device" />
      <DossierTable headers={['Question', 'Answer']} rows={duRows} />
    </>,
  );
}

function Section3({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_03_applicant_information');
  return (
    <NumberedSection prefix="Section" num={3} title="Applicant Information">
      <Field label="Applicant (firm) name:" value={s.firm_name} />
      <Field
        label="Establishment registration number:"
        value={s.establishment_registration_number}
      />
      <Field label="Owner/Operator number:" value={s.owner_operator_number} />
      <Field label="Street address:" value={s.street_address} />
      <Field
        label="U.S. Agent (required for foreign applicants):"
        value={s.us_agent}
      />
      <Field
        label="Official correspondent / contact person:"
        value={s.official_correspondent}
      />
    </NumberedSection>
  );
}

function Section4({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_04_pre_submission_correspondence');
  return (
    <NumberedSection
      prefix="Section"
      num={4}
      title="Pre-Submission Correspondence & Previous Regulator Interaction"
    >
      <Gating
        question="Has the applicant submitted a Pre-Submission (Q-Sub), Submission Issue Request, or any prior interaction with FDA for this device?"
        section={s}
      />
    </NumberedSection>
  );
}

function Section5({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_05_consensus_standards');
  const stdRows = Array.isArray(s.standards)
    ? (s.standards as Record<string, unknown>[]).map((r) => [
        r.fda_recognition_number,
        r.standard_designation,
        r.title,
        r.conformance,
      ])
    : [];
  return (
    <NumberedSection prefix="Section" num={5} title="Consensus Standards">
      <FieldGroup label="Standards entered via eSTAR dropdown (FDA-recognized):">
        <DossierTable
          headers={['FDA Rec. #', 'Standard', 'Title', 'Conformance']}
          rows={stdRows}
        />
      </FieldGroup>
      <FieldProse
        label="Declaration of Conformity (auto-generated for eSTAR):"
        body={prose(s.declaration_of_conformity)}
      />
    </NumberedSection>
  );
}

// Lockstep with backend/services/dossier/estar_renderer/_510k.py §6 — FDA
// "Format for Traditional and Abbreviated 510(k)s" §(10).
function Section6({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_06_device_description');
  // Backward-compat: an old run carrying the legacy free-narrative description.
  const legacy = prose(s.detailed_description);
  const legacyStr = typeof legacy === 'string' ? legacy : '';
  const hasLegacy = legacyStr.trim().length > 0 && !legacyStr.trim().startsWith('__PLACEHOLDER__');
  if (hasLegacy) {
    return (
      <NumberedSection prefix="Section" num={6} title="Device Description">
        <Field label="Device trade name / proprietary name:" value={s.trade_name} />
        <Field label="Common or usual name:" value={s.common_name} />
        <Field label="Models / sizes included in this submission:" value={s.models_included} />
        <FieldProse label="Device description:" body={legacy} />
      </NumberedSection>
    );
  }
  const tcc = Array.isArray(s.tissue_contacting_components)
    ? (s.tissue_contacting_components as unknown[])
    : [];
  const tccRows: unknown[][] = tcc
    .filter((c): c is Record<string, unknown> => typeof c === 'object' && c !== null)
    .map((c) => [c.component, c.material]);
  return (
    <NumberedSection prefix="Section" num={6} title="Device Description">
      <Field label="Device trade name / proprietary name:" value={s.trade_name} />
      <Field label="Common or usual name:" value={s.common_name} />
      <Field label="Models / sizes included in this submission:" value={s.models_included} />
      <Field label="Accessories included in this submission:" value={s.accessories} />
      <Section6SamdFields s={s} />
      <FieldProse
        label="Performance specifications:"
        body={prose(s.performance_specifications)}
        fallbackHint="device performance specifications"
      />
      <FieldProse
        label="Device design requirements:"
        body={prose(s.design_requirements)}
        fallbackHint="brief device design requirements"
      />
      <FieldGroup label="Tissue-contacting components & materials:">
        <DossierTable headers={['Component', 'Material']} rows={tccRows} />
      </FieldGroup>
      <Field
        label="Diagrams / dimensions / tolerances / schematics:"
        value={s.diagrams_note}
        fallbackHint="reference to diagrams / dimensions / tolerances / schematics"
      />
    </NumberedSection>
  );
}

function Section7({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_07_indications_for_use');
  return (
    <NumberedSection
      prefix="Section"
      num={7}
      title="Proposed Indications for Use (Form FDA 3881)"
    >
      <FieldProse
        label="Indications for Use statement (per 21 CFR 814.20(b)(3)(i)):"
        body={prose(s.indications_statement)}
      />
      <Field
        label="Prescription use (Rx) — 21 CFR 801.109:"
        value={s.prescription_use}
      />
      <Field label="Over-the-counter use:" value={s.otc_use} />
      <Field
        label="Life-supporting or life-sustaining device?"
        value={s.life_supporting}
      />
      <Field label="Intended for pediatric use?" value={s.pediatric_use} />
    </NumberedSection>
  );
}

function Section8({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_08_classification');
  return (
    <NumberedSection prefix="Section" num={8} title="Classification">
      <Field label="Regulation number:" value={s.regulation_number} />
      <Field label="Classification name:" value={s.classification_name} />
      <Field label="Product code:" value={s.product_code} />
      <Field label="Device class:" value={s.device_class} />
      <Field label="Review panel:" value={s.review_panel} />
      <Field label="Subject to 510(k):" value={s.subject_to_510k} />
      <Field label="Subject to PMA:" value={s.subject_to_pma} />
      <Field
        label="Class II special controls applicable:"
        value={s.special_controls}
      />
      <Field label="Combination product?" value={s.combination_product} />
      <Field label="Companion diagnostic?" value={s.companion_diagnostic} />
      <RegaraNote
        body={s.classification_source}
        caption="How REGARA determined this classification (for your verification):"
      />
    </NumberedSection>
  );
}

function Section9({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_09_predicates_and_substantial_equivalence');
  const seRows = Array.isArray(s.comparison_table)
    ? (s.comparison_table as Record<string, unknown>[]).map((r) => [
        r.characteristic,
        r.subject_device,
        r.predicate_device,
        r.same_or_different,
      ])
    : [];
  return (
    <NumberedSection
      prefix="Section"
      num={9}
      title="Predicates and Substantial Equivalence"
    >
      <Field label="Primary predicate 510(k) number:" value={s.primary_k_number} />
      <Field label="Primary predicate trade name:" value={s.primary_trade_name} />
      <Field label="Primary predicate manufacturer:" value={s.primary_manufacturer} />
      <Field label="Primary predicate clearance date:" value={s.primary_clearance_date} />
      <Field label="Reference device(s) used (if any):" value={s.reference_devices} />
      <RegaraNote body="Inclusion of reference device(s) is up to the sponsor/applicant." />
      <RegaraProseNote label="Rationale for predicate selection:" body={prose(s.rationale)} />
      <FieldGroup label="Side-by-side comparison table:">
        <DossierTable
          headers={[
            'Characteristic',
            'Subject device',
            'Predicate device',
            'Same / Different',
          ]}
          rows={seRows}
        />
      </FieldGroup>
      <FieldProse label="Discussion of any differences:" body={prose(s.discussion)} />
    </NumberedSection>
  );
}

function Section10({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_10_design_controls');
  const isYes = String(s.gating_response ?? 'No') === 'Yes';
  return (
    <NumberedSection
      prefix="Section"
      num={10}
      title="Design/Special Controls, Risks to Health, and Mitigation Measures"
    >
      {isYes ? (
        <>
          <Field label="Applies to:" value="Special 510(k) submission" />
          <FieldProse label="Device changes:" body={prose(s.device_changes)} />
          <FieldProse
            label="Risk analysis method:"
            body={prose(s.risk_analysis_method)}
          />
          <FieldProse
            label="Risk analysis results:"
            body={prose(s.risk_analysis_results)}
          />
          <FieldProse
            label="Risk control measures:"
            body={prose(s.risk_control_measures)}
          />
        </>
      ) : (
        <Gating
          question="Is this a Special 510(k) submission requiring Design Controls and risk analysis documentation?"
          section={s}
        />
      )}
    </NumberedSection>
  );
}

function Section11({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_11_labeling');
  const components = Array.isArray(s.labeling_components)
    ? (s.labeling_components as unknown[])
    : [];
  const checklist = Array.isArray(s.required_content_checklist)
    ? (s.required_content_checklist as unknown[])
    : [];
  return (
    <NumberedSection prefix="Section" num={11} title="Labeling">
      <FieldGroup
        label="Labeling components included:"
        fallbackHint="Labeling components included"
      >
        {components.length > 0 ? <Checklist items={components} /> : null}
      </FieldGroup>
      <FieldGroup
        label="Required content per 21 CFR 801 — confirmed present:"
        fallbackHint="Required content per 21 CFR 801"
      >
        {checklist.length > 0 ? <Checklist items={checklist} /> : null}
      </FieldGroup>
      <FieldProse label="Device label text:" body={prose(s.device_label_text)} />
      <FieldProse
        label="Instructions for Use (IFU) text:"
        body={prose(s.ifu_text)}
      />
    </NumberedSection>
  );
}

function Section12({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_12_reprocessing');
  return (
    <NumberedSection prefix="Section" num={12} title="Reprocessing">
      <Field label="Is the device reusable?" value={canonNA(s.is_reusable)} />
      <Field label="Reprocessing method:" value={canonNA(s.reprocessing_method)} />
      <Field
        label="Is reprocessing validated?"
        value={s.reprocessing_validated}
      />
      <FieldProse
        label="Reprocessing validation summary:"
        body={prose(s.validation_summary)}
      />
    </NumberedSection>
  );
}

function Section13({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_13_sterility');
  return (
    <NumberedSection prefix="Section" num={13} title="Sterility">
      <Field
        label="Is the device provided sterile to the end user?"
        value={canonNA(s.provided_sterile)}
      />
      <Field label="Sterilization method:" value={canonNA(s.sterilization_method)} />
      <FieldProse
        label="Sterility validation:"
        body={prose(s.sterility_validation)}
      />
    </NumberedSection>
  );
}

function Section14({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_14_shelf_life');
  return (
    <NumberedSection prefix="Section" num={14} title="Shelf Life">
      <Field
        label="Does the device have a defined shelf life?"
        value={canonNA(s.has_shelf_life)}
      />
      <Field label="Shelf life duration:" value={canonNA(s.shelf_life_duration)} />
      <FieldProse
        label="Shelf life validation:"
        body={prose(s.shelf_life_validation)}
      />
    </NumberedSection>
  );
}

function Section15({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_15_biocompatibility');
  const bioRows = Array.isArray(s.endpoints_table)
    ? (s.endpoints_table as Record<string, unknown>[]).map((r) => [
        r.endpoint,
        r.method_standard,
        r.sample,
        r.result,
        r.date,
      ])
    : [];
  return (
    <NumberedSection prefix="Section" num={15} title="Biocompatibility">
      <Field
        label="Does the device or any component contact the patient directly or indirectly?"
        value={s.patient_contact}
      />
      <Field label="ISO 10993-1 categorization:" value={s.iso_categorization} />
      <FieldGroup label="Biological endpoints evaluated:">
        <DossierTable
          headers={['Endpoint', 'Method (standard)', 'Sample', 'Result', 'Date']}
          rows={bioRows}
        />
      </FieldGroup>
      <Field label="Testing laboratory:" value={s.testing_laboratory} />
      <FieldProse
        label="Detailed test results:"
        body={prose(s.detailed_results)}
      />
      <FieldProse
        label="Biocompatibility conclusion:"
        body={prose(s.conclusion)}
      />
    </NumberedSection>
  );
}

function Section16({ dossier }: { dossier: Record<string, unknown> }) {
  return <Section16SoftwareDoc dossier={dossier} sectionKey="section_16_software_firmware" num={16} />;
}

function Section17({ dossier }: { dossier: Record<string, unknown> }) {
  return <Section17CyberInteropDoc dossier={dossier} sectionKey="section_17_cybersecurity" num={17} />;
}

function Section18({ dossier }: { dossier: Record<string, unknown> }) {
  return (
    <NumberedSection
      prefix="Section"
      num={18}
      title="EMC, Electrical, Mechanical, Wireless and Thermal Safety"
    >
      <Gating
        question="Does the subject device incorporate any electrical, electronic, electromechanical, or wireless components?"
        section={obj(dossier, 'section_18_electrical_safety_emc')}
      />
    </NumberedSection>
  );
}

function Section19({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_19_performance_testing');
  const bench = obj(s, 'bench_testing');
  const animal = obj(s, 'animal_testing');
  const clinical = obj(s, 'clinical_testing');

  const benchRows = Array.isArray(bench.performance_test_table)
    ? (bench.performance_test_table as Record<string, unknown>[]).map((r) => [
        r.number,
        r.test,
        r.acceptance_criterion,
        r.n,
        r.result,
      ])
    : [];

  const clinicalIsYes = String(clinical.gating_response ?? 'No') === 'Yes';

  return (
    <NumberedSection prefix="Section" num={19} title="Performance Testing">
      {/* Bench */}
      <h4 className="text-[14px] font-bold text-navy mt-2 mb-2">
        Non-Clinical Bench Performance Testing
      </h4>
      <Field
        label="Was bench performance testing used to support this submission?"
        value={bench.bench_used}
      />
      <Field label="Testing facility:" value={bench.testing_facility} />
      <FieldGroup label="Performance test summary:">
        <DossierTable
          headers={['#', 'Test', 'Acceptance criterion', 'n', 'Result']}
          rows={benchRows}
        />
      </FieldGroup>
      <FieldProse
        label="Detailed test results:"
        body={prose(bench.detailed_results)}
      />
      <FieldProse label="Conclusion:" body={prose(bench.conclusion)} />

      {/* Animal */}
      <h4 className="text-[14px] font-bold text-navy mt-4 mb-2">
        Animal Performance Studies
      </h4>
      <Gating
        question="Were animal studies conducted to support this submission?"
        section={animal}
      />

      {/* Clinical */}
      <h4 className="text-[14px] font-bold text-navy mt-4 mb-2">
        Clinical Performance Studies
      </h4>
      {clinicalIsYes ? (
        <>
          <Field
            label="Were clinical studies conducted or relied upon?"
            value="Yes."
          />
          {prose(clinical.study_summary) != null && (
            <FieldProse
              label="Clinical study summary:"
              body={prose(clinical.study_summary)}
            />
          )}
          {prose(clinical.efficacy_results) != null && (
            <FieldProse
              label="Efficacy results:"
              body={prose(clinical.efficacy_results)}
            />
          )}
          {prose(clinical.safety_analysis) != null && (
            <FieldProse
              label="Safety analysis:"
              body={prose(clinical.safety_analysis)}
            />
          )}
          {prose(clinical.risk_benefit_analysis) != null && (
            <FieldProse
              label="Risk-benefit analysis:"
              body={prose(clinical.risk_benefit_analysis)}
            />
          )}
          {prose(clinical.statistical_analysis) != null && (
            <FieldProse
              label="Statistical analysis plan:"
              body={prose(clinical.statistical_analysis)}
            />
          )}
        </>
      ) : (
        <Gating
          question="Were clinical studies conducted, or is clinical data being relied upon, to support this submission?"
          section={clinical}
        />
      )}
    </NumberedSection>
  );
}

function Section20({ dossier }: { dossier: Record<string, unknown> }) {
  const refs = Array.isArray(dossier.section_20_references)
    ? (dossier.section_20_references as unknown[])
    : [];
  return (
    <NumberedSection prefix="Section" num={20} title="References">
      <FieldGroup
        label="References:"
        fallbackHint="Literature references and FDA records cited in this submission"
      >
        {refs.length > 0 ? (
          <ol className="list-decimal pl-5 m-0 space-y-1 text-[13.5px]">
            {refs.map((s, i) => (
              <ReferenceItem key={i} src={s} />
            ))}
          </ol>
        ) : null}
      </FieldGroup>
    </NumberedSection>
  );
}

function ReferenceItem({ src }: { src: unknown }) {
  if (!src || typeof src !== 'object') return <li>{String(src)}</li>;
  const s = src as {
    identifier?: string;
    title?: string;
    url?: string;
    tool?: string;
    note?: string;
  };
  return (
    <li>
      {s.identifier && (
        <span className="font-mono text-[12.5px] text-electric mr-1">
          {s.identifier}
        </span>
      )}
      {s.url && s.title ? (
        <a
          href={s.url}
          target="_blank"
          rel="noopener"
          className="text-navy underline decoration-line-strong hover:decoration-electric"
        >
          {s.title}
        </a>
      ) : s.url ? (
        <a
          href={s.url}
          target="_blank"
          rel="noopener"
          className="text-navy underline decoration-line-strong hover:decoration-electric"
        >
          {s.url}
        </a>
      ) : s.title ? (
        <span>{s.title}</span>
      ) : null}
      {s.tool && (
        <span className="ml-2 text-[10.5px] font-bold uppercase tracking-[0.1em] text-muted bg-[#f3f4f6] border border-line rounded-full py-0.5 px-2">
          {s.tool}
        </span>
      )}
      {s.note && <span className="text-muted"> — {s.note}</span>}
    </li>
  );
}

function Section21({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_21_administrative_documentation');
  const summary = obj(s, 'five_ten_k_summary');
  const tas = obj(s, 'truthful_accurate_statement');
  const fc = obj(s, 'financial_certification');
  return (
    <NumberedSection
      prefix="Section"
      num={21}
      title="Administrative Documentation"
    >
      {/* 510(k) Summary */}
      <h4 className="text-[14px] font-bold text-navy mt-2 mb-2">
        510(k) Summary (per 21 CFR 807.92)
      </h4>
      <FiveTenKSummaryBlock dossier={dossier} summary={summary} />

      {/* T&A Statement */}
      <h4 className="text-[14px] font-bold text-navy mt-4 mb-2">
        Truthful and Accurate Statement (per 21 CFR 807.87(k))
      </h4>
      <TruthfulAccurateBlock tas={tas} />

      {/* Financial Certification */}
      <h4 className="text-[14px] font-bold text-navy mt-4 mb-2">
        Financial Certification or Disclosure (21 CFR Part 54)
      </h4>
      <Gating
        question="Did the applicant rely on clinical studies subject to 21 CFR Part 54 (financial disclosure by clinical investigators)?"
        section={fc}
      />
    </NumberedSection>
  );
}

function FiveTenKSummaryBlock({
  dossier,
  summary,
}: {
  dossier: Record<string, unknown>;
  summary: Record<string, unknown>;
}) {
  // Prefer-legacy: a real single-blob body (already-stored dossiers) renders as-is.
  const legacy = summary.body;
  if (typeof legacy === 'string' && legacy.trim() && !legacy.trim().startsWith('__PLACEHOLDER__')) {
    return (
      <FieldProse label="510(k) Summary:" body={legacy} fallbackHint="510(k) Summary per 21 CFR 807.92" />
    );
  }
  // Structured layout (K231948) — LOCKSTEP with backend _estar_s21_510k_summary_body.
  // Factual elements pulled from the validated sections (§2 sponsor/device-id, §3 firm,
  // §6 model, §7 indications, §9 predicate + SE table); the agent supplies only the
  // condensed prose + the two structural tables (main_components, software_vv_summary).
  const admin = obj(obj(dossier, 'section_02_cover_letter'), 'administrative_information');
  const applicant = obj(dossier, 'section_03_applicant_information');
  const s6 = obj(dossier, 'section_06_device_description');
  const s9 = obj(dossier, 'section_09_predicates_and_substantial_equivalence');
  const s16 = obj(dossier, 'section_16_software_firmware');
  const cc = obj(admin, 'company_contact');
  const sc = obj(admin, 'submission_contact');
  const indications = prose(obj(obj(dossier, 'section_07_indications_for_use'), 'indications_statement'));
  const subType = obj(dossier, 'section_01_submission_type').submission_type;
  const isReal = (v: unknown): v is string =>
    typeof v === 'string' && v.trim().length > 0 && !v.trim().startsWith('__PLACEHOLDER__');
  const firm = isReal(applicant.firm_name) ? applicant.firm_name : '';
  const trade = Array.isArray(admin.device_trade_names)
    ? (admin.device_trade_names as unknown[]).filter((t) => typeof t === 'string').join(', ')
    : '';
  const hasSoftware = String(s16.gating_response ?? '').trim().toLowerCase() === 'yes';
  // Opening statement — guarded (mirror backend): needs a real firm + trade.
  const stmt =
    firm && isReal(trade)
      ? `This 510(k) Summary is being submitted for ${firm}’s ${trade}${
          isReal(subType) ? ` ${subType}` : ''
        } in accordance with requirement of 21 CFR 807.92.`
      : 'This 510(k) Summary is submitted in accordance with requirement of 21 CFR 807.92.';
  // Table 1: Main Components — only rows with real content (mirror backend).
  const compRows = (Array.isArray(summary.main_components) ? (summary.main_components as unknown[]) : [])
    .map((r) => (r ?? {}) as Record<string, unknown>)
    .filter((c) => isReal(c.component) || isReal(c.function))
    .map((c) => [c.component, c.function]);
  const seRows = (Array.isArray(s9.comparison_table) ? (s9.comparison_table as unknown[]) : []).map((r) => {
    const row = (r ?? {}) as Record<string, unknown>;
    return [row.characteristic, row.subject_device, row.predicate_device, row.same_or_different];
  });
  const H = ({ n, t }: { n: number; t: string }) => (
    <h5 className="text-[13px] font-bold text-navy mt-3 mb-1">
      {n}. {t}
    </h5>
  );
  const Sub = ({ t }: { t: string }) => (
    <div className="text-[12px] font-semibold text-navy mt-2 mb-1">{t}</div>
  );
  const Cap = ({ t }: { t: string }) => (
    <div className="text-center italic text-[12px] text-muted my-1">{t}</div>
  );
  return (
    <div className="text-[13.5px]">
      {/* No standalone "510(k) Summary" title — the §21 block already renders the
          "510(k) Summary (per 21 CFR 807.92)" sub-content heading above (avoid duplication). */}
      <p className="m-0 mb-1">{stmt}</p>
      <p className="m-0 mb-2">
        The assigned 510(k) Number: <Placeholder hint="K-number assigned by FDA" />
      </p>
      <H n={1} t="Date of Preparation" />
      <Field label="Date prepared:" value={undefined} fallbackHint="Date the 510(k) Summary is finalized" />
      <H n={2} t="Sponsor" />
      <Field label="Firm name:" value={applicant.firm_name} />
      <Field label="Address:" value={admin.sponsor_address} />
      <Field label="Establishment Registration Number:" value={admin.establishment_registration} />
      <Field label="Contact person:" value={cc.name} />
      <Field label="Position:" value={cc.title} />
      <Field label="Telephone:" value={cc.phone} />
      <Field label="Email:" value={cc.email} />
      <H n={3} t="Submission Correspondent" />
      <Field label="Firm name:" value={applicant.firm_name} />
      <Field label="Address:" value={admin.sponsor_address} />
      <Field label="Name:" value={sc.name} />
      <Field label="Position:" value={sc.title} />
      <Field label="Telephone:" value={sc.phone} />
      <Field label="Email:" value={sc.email} />
      <H n={4} t="Proposed Device Identification" />
      <Field label="Trade / proprietary name:" value={trade} />
      <Field label="Common / usual name:" value={admin.device_common_name} />
      <Field label="Model:" value={s6.models_included} />
      <Sub t="Regulatory Information" />
      <Field label="Classification name:" value={admin.classification_name} />
      <Field label="Classification:" value={admin.device_class} />
      <Field label="Product code:" value={admin.device_product_code} />
      <Field label="Regulation number:" value={admin.classification_regulation} />
      <Field label="Review panel:" value={admin.device_panel} />
      <Sub t="Indications for Use Statement:" />
      <FieldProse body={indications} fallbackHint="Indications for Use (verbatim, per §7)" />
      <H n={5} t="Device Description" />
      <FieldProse
        body={prose(summary.device_description_summary)}
        fallbackHint="Condensed device description (summarize §6)"
      />
      {compRows.length > 0 && (
        <>
          <Cap t="Table 1: Main Components Introduction" />
          <DossierTable headers={['Component Name', 'Function']} rows={compRows} />
        </>
      )}
      <H n={6} t="Predicate Device Identification" />
      <Sub t="Primary Predicate Device:" />
      <Field label="510(k) Number:" value={s9.primary_k_number} />
      <Field label="Product Name:" value={s9.primary_trade_name} />
      <Field label="Manufacturer:" value={s9.primary_manufacturer} />
      <Field label="Clearance date:" value={s9.primary_clearance_date} />
      {isReal(s9.reference_devices) && (
        <>
          <Sub t="Reference Device(s):" />
          <Field label="Reference device(s):" value={s9.reference_devices} />
        </>
      )}
      <H n={7} t="Non-Clinical Testing" />
      <FieldProse
        body={prose(summary.nonclinical_summary)}
        fallbackHint="Non-clinical / bench testing (summarize §19 / §5)"
      />
      {hasSoftware && (
        <>
          <Sub t="7a. Software Verification and Validation Testing" />
          <FieldProse
            body={prose(summary.software_vv_summary)}
            fallbackHint="Software V&V testing summary (summarize §16)"
          />
        </>
      )}
      <H n={8} t="Clinical Testing" />
      <FieldProse
        body={prose(summary.clinical_statement)}
        fallbackHint="Clinical testing statement (summarize §19 clinical)"
      />
      <H n={9} t="Biocompatibility" />
      <FieldProse
        body={prose(summary.biocompatibility_statement)}
        fallbackHint="Biocompatibility statement (summarize §15)"
      />
      <H n={10} t="Substantially Equivalent (SE) Comparison" />
      <Cap t="Table 2: Comparison" />
      <DossierTable
        headers={['Characteristic', 'Subject device', 'Predicate device', 'Same / Different']}
        rows={seRows}
      />
      <H n={11} t="Substantially Equivalent Conclusion" />
      <FieldProse
        body={prose(summary.se_conclusion)}
        fallbackHint="Substantial-equivalence conclusion (summarize §9)"
      />
    </div>
  );
}

function TruthfulAccurateBlock({ tas }: { tas: Record<string, unknown> }) {
  const today = new Date().toISOString().slice(0, 10);
  const statement = tas.statement;
  const lines: { text: string | unknown; hint: string | null }[] = [
    { text: statement, hint: 'Statement of certification' },
    { text: '', hint: null },
    { text: 'Signature: [electronic signature on file]', hint: null },
    { text: `Name: ${(tas.signatory_name as string) ?? ''}`, hint: 'Signatory name' },
    { text: `Title: ${(tas.signatory_title as string) ?? ''}`, hint: 'Signatory title' },
    { text: `Date: ${(tas.date as string) || today}`, hint: null },
  ];
  return (
    <div className="text-[13.5px] leading-[1.6]">
      {lines.map((line, i) => {
        const text =
          line.text == null
            ? ''
            : typeof line.text === 'string'
              ? line.text
              : String(line.text);
        if (text === '' && line.hint == null) {
          return (
            <p key={i} className="m-0 my-1">
              &nbsp;
            </p>
          );
        }
        if (text === '' && line.hint) {
          return (
            <p key={i} className="m-0 my-1">
              <Placeholder hint={line.hint} />
            </p>
          );
        }
        return (
          <p key={i} className="m-0 my-1">
            {withInlinePlaceholders(text)}
          </p>
        );
      })}
    </div>
  );
}

function Section22({ dossier }: { dossier: Record<string, unknown> }) {
  return (
    <NumberedSection
      prefix="Section"
      num={22}
      title="Amendment/Additional Information (AI) Response"
    >
      <Gating
        question="Is this submission a response to an FDA Additional Information request?"
        section={obj(dossier, 'section_22_amendment_response')}
      />
    </NumberedSection>
  );
}
